import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Package } from 'lucide-react';
import axiosInstance from '../api/axios';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch categories (nur nicht-leere für Kundenansicht)
      const catResponse = await axiosInstance.get('/categories-detailed');
      const visible = (catResponse.data || []).filter((c) => (c.product_count || 0) > 0);
      const sorted = visible.sort((a, b) => (a.order || 0) - (b.order || 0));
      setCategories(sorted);

      // Fetch all products
      const prodResponse = await axiosInstance.get('/products');
      const products = prodResponse.data || [];

      // Group products by category
      const grouped = {};
      sorted.forEach(cat => {
        const catName = typeof cat === 'string' ? cat : cat.name;
        grouped[catName] = products.filter(p => p.category === catName);
      });
      setProductsByCategory(grouped);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback
      try {
        const fallback = await axiosInstance.get('/categories?hide_empty=true');
        const cats = fallback.data.map((name, index) => ({
          id: index,
          name: typeof name === 'string' ? name : name.name,
          order: index
        }));
        setCategories(cats);
      } catch (e) {
        console.error('Fallback failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const scrollContainer = (containerId, direction) => {
    const container = document.getElementById(containerId);
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F0EB' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B7355] to-[#6D5A47] text-white py-6 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold mb-1">Kategorien</h1>
          <p className="text-white/80 text-sm">Entdecken Sie unsere Produktkategorien</p>
        </div>
      </div>

      {/* Categories with Products */}
      <div className="max-w-7xl mx-auto px-4 py-4 space-y-6">
        {categories.map((category) => {
          const catName = typeof category === 'string' ? category : category.name;
          const products = productsByCategory[catName] || [];
          const containerId = `cat-scroll-${catName.replace(/\s+/g, '-')}`;

          return (
            <div key={category.id || catName} className="bg-white shadow-sm">
              {/* Category Header */}
              <div className="p-3 border-b border-[#E8E0D8]" style={{ backgroundColor: '#F5EDE4' }}>
                <div className="flex items-center justify-between gap-3">
                  <Link 
                    to={`/products?category=${encodeURIComponent(catName)}`}
                    className="hover:text-[#8B7355] transition-colors min-w-0 flex-1"
                  >
                    <h2 className="text-sm font-bold text-[#5D4E3C] truncate">{catName}</h2>
                  </Link>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => scrollContainer(containerId, 'left')}
                      className="px-2 py-1 flex items-center justify-center bg-[#C4A084] hover:bg-[#8B7355] transition-colors text-white"
                      aria-label="Nach links scrollen"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => scrollContainer(containerId, 'right')}
                      className="px-2 py-1 flex items-center justify-center bg-[#C4A084] hover:bg-[#8B7355] transition-colors text-white"
                      aria-label="Nach rechts scrollen"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <Link
                      to={`/products?category=${encodeURIComponent(catName)}`}
                      className="px-2 py-1 bg-[#8B7355] hover:bg-[#6D5A47] text-white text-xs font-medium transition-colors"
                    >
                      Alle
                    </Link>
                  </div>
                </div>
              </div>

              {/* Products Row - Horizontal Scroll */}
              <div 
                id={containerId}
                className="flex gap-3 overflow-x-auto p-3 scrollbar-hide"
                style={{ scrollBehavior: 'smooth' }}
              >
                {products.length > 0 ? (
                  products.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      className="flex-shrink-0 group flex flex-col"
                      style={{ width: '120px' }}
                    >
                      <div className="relative overflow-hidden bg-gray-100" style={{ height: '120px' }}>
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={imgVariant(product.images[0], { w: 400 })}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#F5EDE4]">
                            <Package className="h-8 w-8 text-[#D4C4B0]" />
                          </div>
                        )}
                        {!(product.stock > 0) && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-xs font-medium">Ausverkauft</span>
                          </div>
                        )}
                      </div>
                      <div className="pt-2 flex flex-col flex-1">
                        <p className="text-xs text-[#5D4E3C] line-clamp-2 leading-tight h-8">{product.name}</p>
                        <span 
                          className="inline-block mt-auto px-2 py-0.5 text-white text-xs font-bold self-start"
                          style={{ backgroundColor: '#C4A084' }}
                        >
                          {formatEUR(product.price)}
                          {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                            <span className="font-normal ml-0.5">/ {product.price_quantity} St.</span>
                          )}
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="flex items-center justify-center w-full py-6 text-[#8B7355] text-sm">
                    Noch keine Produkte in dieser Kategorie
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoriesPage;
